// delegate-shell-theme
// Embed layout store (docs/delegate/THEME.md section 10.2). The bridge
// drives it; React reads it through useDelegateEmbed(). Pure state plus the
// four layout attributes on <html>; no message handling here.
import type { EmbedLayout } from "./theme-contract";

export type EmbedState = "standalone" | "provisional" | "confirmed";

export interface DelegateEmbed {
  state: EmbedState;
  /** "shell": V2 draws the window chrome, Meet hides its own top bar/brand. */
  chrome: EmbedLayout["chrome"];
  /** "rail": Meet renders the V2 WebOSAppShell-style side rail. */
  nav: EmbedLayout["nav"];
  density: EmbedLayout["density"];
}

/** Standalone Meet draws its own chrome and nav, exactly as upstream. */
export const STANDALONE_EMBED: Readonly<DelegateEmbed> = Object.freeze({
  state: "standalone",
  chrome: "app",
  nav: "app",
  density: "regular",
});

export const EMBED_ATTRIBUTES = [
  "data-delegate-embed",
  "data-delegate-chrome",
  "data-delegate-nav",
  "data-delegate-density",
] as const;

let current: Readonly<DelegateEmbed> = STANDALONE_EMBED;
const listeners = new Set<() => void>();

export function getDelegateEmbed(): Readonly<DelegateEmbed> {
  return current;
}

export function subscribeDelegateEmbed(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function writeAttributes(root: HTMLElement, next: DelegateEmbed) {
  if (next.state === "standalone") {
    for (const name of EMBED_ATTRIBUTES) root.removeAttribute(name);
    return;
  }
  root.setAttribute("data-delegate-embed", next.state === "confirmed" ? "1" : "provisional");
  root.setAttribute("data-delegate-chrome", next.chrome);
  root.setAttribute("data-delegate-nav", next.nav);
  root.setAttribute("data-delegate-density", next.density);
}

/**
 * Sets the embed state and writes the matching <html> attributes before
 * notifying, so React never renders a layout the CSS has not caught up with.
 * `writeDom: false` only updates the store (used when the bridge restores an
 * exact DOM snapshot itself).
 */
export function setDelegateEmbed(
  state: EmbedState,
  layout?: Pick<EmbedLayout, "chrome" | "nav" | "density">,
  options: { writeDom?: boolean } = {}
): void {
  const next: DelegateEmbed =
    state === "standalone" || !layout
      ? { ...STANDALONE_EMBED, state }
      : {
          state,
          chrome: layout.chrome,
          nav: layout.nav,
          density: layout.density,
        };
  if (options.writeDom !== false && typeof document !== "undefined") {
    writeAttributes(document.documentElement, next);
  }
  if (
    next.state === current.state &&
    next.chrome === current.chrome &&
    next.nav === current.nav &&
    next.density === current.density
  ) {
    return;
  }
  current = Object.freeze(next);
  for (const listener of Array.from(listeners)) listener();
}

/** Test-only. */
export function resetDelegateEmbedForTests(): void {
  current = STANDALONE_EMBED;
  for (const listener of Array.from(listeners)) listener();
}
